import React from 'react'
import { makeStyles } from 'tss-react/mui'
import { Box, Dialog, DialogTitle, IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { ContractStateProps, ExtensionCategory, ExtensionType } from '../../types'
import Description from './Description'
import { Chip } from 'src/Components/chips'
import ContractState from './ContractState'

interface StyleProps {
  extensionType: ExtensionType
}

const useStyles = makeStyles<StyleProps>()((theme, { extensionType }) => ({
  container: {
    '&  .MuiDialog-paper': {
      width: '90%',
      maxWidth: '800px',
      maxHeight: '700px',
      padding: theme.spacing(4),
      gap: theme.spacing(4),
    },
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: 0,
  },
  body: {
    display: 'flex',
    gap: theme.spacing(4),
    backgroundColor: theme.palette.common.white,
    flexDirection: 'column',
    alignItems: 'center',
    overflow: 'hidden',
  },
  footer: {
    display: 'flex',
    gap: theme.spacing(4),
    flexDirection: 'column',
    alignItems: 'center',
    overflow: 'hidden',
    width: '100%',
  },
  description: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    overflowY: 'auto',
    scrollbarWidth: 'thin' /** Firefox */,
    '&::-webkit-scrollbar': {
      position: 'absolute',
      width: '6px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.grey.grey06,
      height: '80%',
      borderRadius: 12,
    },
    [theme.breakpoints.down('md')]: {
      marginRight: theme.spacing(2),
    },
  },
  closeIcon: {
    color: theme.palette.grey.grey00,
  },
}))

interface Props {
  extensionType: ExtensionType
  descriptions: string[]
  headerContent: React.ReactElement
  bodyContent?: React.ReactElement
  footerContent: React.ReactElement
  onClose: () => void
  place: number
  open: boolean
  category: ExtensionCategory
  contractStateProps?: ContractStateProps
  linkHref?: string
  onLinkClick?: () => void
}

/**
 * A modal component to display detailed extension information.
 */
const DetailsModal: React.FC<Props> = ({
  extensionType,
  descriptions,
  headerContent,
  bodyContent,
  footerContent,
  onClose,
  category,
  contractStateProps,
  linkHref,
  onLinkClick,
}) => {
  const { classes } = useStyles({ extensionType })
  const categoryName = `installation-extension.category.${category?.name}`

  return (
    <Dialog open={true} onClose={onClose} className={classes.container}>
      <DialogTitle className={classes.header}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {headerContent}
          <IconButton className={classes.closeIcon} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <Chip textKey={categoryName} paletteColor={category.paletteColor} isLoading={false} variant={category.variant} />
        </Box>
      </DialogTitle>
      <Box className={classes.body}>
        <Description textParts={descriptions} style={classes.description} linkHref={linkHref} onLinkClick={onLinkClick} />
        {bodyContent}
        <Box className={classes.footer}>
          {footerContent}
          <ContractState {...contractStateProps} />
        </Box>
      </Box>
    </Dialog>
  )
}

export default DetailsModal
